import React, { useState, useRef } from 'react';
import { Button, FormGroup, FormLabel } from 'react-bootstrap';
import { useText } from '../context/TextContext';
import { AdvancedButton } from './AdvancedButton';
import { FileAttachment } from '../models/shared/DealPublicInput';

const FILE_DROP_AREA_ID = 'file-drop-area';

interface Props {
    attachments: FileAttachment[];
    onAttachmentsChange: (attachments: FileAttachment[]) => void;
    className?: string;
}

export default function PublicNewDealFileAttachments({ attachments, onAttachmentsChange, className }: Props) {
    const text = useText();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef<number>(0);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        addNewFiles(Array.from(files));

        // Reset the input value so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const addNewFiles = (files: File[]) => {
        // Filter to only image files
        const imageFiles = files.filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) return;

        const newAttachments = imageFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            id: Math.random().toString(36).substring(2, 11)
        }));

        onAttachmentsChange([...attachments, ...newAttachments]);
    };

    const handleRemove = (id: string) => {
        const updatedAttachments = attachments.filter(attachment => attachment.id !== id);
        onAttachmentsChange(updatedAttachments);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            addNewFiles(Array.from(files));
        }
    };

    const handleCapture = () => {
        const input = fileInputRef.current;
        if (!input) return;

        input.accept = "image/*";
        input.capture = "environment";
        input.click();
    };

    return (
        <FormGroup className={className}>

            <FormLabel>
                <div className='d-flex align-items-center gap-2'>

                    {text.photos}
                    <AdvancedButton
                        variant="primary"
                        size="sm"
                        icon="bi-camera"
                        onClick={handleCapture}
                    >
                        {text.takePhoto}
                    </AdvancedButton>

                </div>
            </FormLabel>


            <div
                id={FILE_DROP_AREA_ID}
                className={`border rounded p-3 ${isDragging ? 'bg-white' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    multiple
                />

                {attachments.length === 0 ? (
                    <div className="text-center p-4">
                        <i className="bi bi-images fs-2 text-muted mb-2"></i>
                        <p className="text-muted mb-0">{text.dragImagesHere}</p>
                    </div>
                ) : (
                    <div className="d-flex flex-wrap gap-3">
                        {attachments.map((attachment) => (
                            <div key={attachment.id} className="position-relative" style={{ width: '100px', height: '100px' }}>
                                <img
                                    src={attachment.preview}
                                    alt={attachment.file.name}
                                    className="rounded shadow-sm"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: '22px', height: '22px', marginTop: '-8px', marginRight: '-8px' }}
                                    onClick={() => handleRemove(attachment.id)}
                                >
                                    <i className="bi bi-x-lg" style={{ fontSize: '12px' }}></i>
                                </Button>
                            </div>
                        ))}
                    </div>
                )}


            </div>
        </FormGroup>
    );
}
